import React, { useState, useEffect, useRef } from 'react';
import { Shield, School, Building2, Briefcase, LogOut, FileText, Upload, Search, CheckCircle, XCircle, User, Mail, Lock, Award, Camera, Download } from 'lucide-react';

const API_BASE_URL = 'https://api.authentichain.live/api';

const AuthentiChain = () => {
  const [userType, setUserType] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [orgType, setOrgType] = useState('school');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [token, setToken] = useState('');
  const [userData, setUserData] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [orgSignupData, setOrgSignupData] = useState({
    type: 'school',
    organizationName: '',
    email: '',
    password: '',
    contactPerson: '',
    phone: '',
    address: ''
  });
  const [studentSignupData, setStudentSignupData] = useState({
    name: '',
    email: '',
    password: '',
    registrationNumber: '',
    address: '',
    postcode: '',
    parentName: '',
    phone: '',
    linkedin: ''
  });
  
  const [certificateForm, setCertificateForm] = useState({
    studentId: '',
    name: '',
    course: '',
    grade: '',
    duration: '',
    completionDate: ''
  });
  const [certificateTemplate, setCertificateTemplate] = useState(null);
  const [studentSearch, setStudentSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  
  const [verificationType, setVerificationType] = useState('document');
  const [verifyHash, setVerifyHash] = useState('');
  const [verifyTemplate, setVerifyTemplate] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  
  const [certificates, setCertificates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');

  useEffect(() => {
    const savedToken = localStorage.getItem('authToken');
    const savedUserType = localStorage.getItem('userType');
    const savedUserData = localStorage.getItem('userData');
    const savedWallet = localStorage.getItem('walletAddress');
    
    if (savedToken && savedUserType && savedUserData) {
      setToken(savedToken);
      setUserType(savedUserType);
      setUserData(JSON.parse(savedUserData));
      setIsLoggedIn(true);
    }
    
    if (savedWallet) {
      setWalletAddress(savedWallet);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && token) {
      fetchUserData();
    }
  }, [isLoggedIn, token, userType]);

  const fetchUserData = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (userType === 'organization') {
        const certsRes = await fetch(`${API_BASE_URL}/organization/certificates`, { headers });
        const certs = await certsRes.json();
        setCertificates(certs);
      } else if (userType === 'student') {
        const certsRes = await fetch(`${API_BASE_URL}/student/certificates`, { headers });
        const certs = await certsRes.json();
        setCertificates(certs);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleLogin = async (e, type) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const endpoint = type === 'organization'
        ? `${API_BASE_URL}/organization/login`
        : `${API_BASE_URL}/student/login`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });

      const data = await response.json();
      
      if (response.ok) {
        setToken(data.token);
        setUserType(type);
        setUserData(data[type] || data.student || data.organization);
        setIsLoggedIn(true);
        
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('userType', type);
        localStorage.setItem('userData', JSON.stringify(data[type] || data.student || data.organization));
        
        alert('Login successful!');
      } else {
        alert(data.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrganizationSignup = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/organization/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...orgSignupData, type: orgType })
      });

      const data = await response.json();
      
      if (response.ok) {
        alert('Registration submitted! Please wait for admin approval.');
        setAuthMode('login');
        setOrgSignupData({
          type: 'school',
          organizationName: '',
          email: '',
          password: '',
          contactPerson: '',
          phone: '',
          address: ''
        });
      } else {
        alert(data.error || 'Registration failed');
      }
    } catch (error) {
      console.error('Signup error:', error);
      alert('Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStudentSignup = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/student/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(studentSignupData)
      });

      const data = await response.json();
      
      if (response.ok) {
        setToken(data.token);
        setUserType('student');
        setUserData(data.student);
        setIsLoggedIn(true);
        
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('userType', 'student');
        localStorage.setItem('userData', JSON.stringify(data.student));
        
        alert('Registration successful!');
      } else {
        alert(data.error || 'Registration failed');
      }
    } catch (error) {
      console.error('Signup error:', error);
      alert('Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const searchStudents = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/students/search?query=${query}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const handleIssueCertificate = async (e) => {
    e.preventDefault();
    
    if (!certificateForm.studentId) {
      alert('Please search and select a student first');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('studentId', certificateForm.studentId);
      formData.append('name', certificateForm.name);
      formData.append('course', certificateForm.course);
      formData.append('grade', certificateForm.grade);
      formData.append('duration', certificateForm.duration);
      formData.append('completionDate', certificateForm.completionDate);
      
      if (certificateTemplate) {
        formData.append('certificate', certificateTemplate);
      }

      console.log('Submitting certificate with studentId:', certificateForm.studentId);

      const response = await fetch(`${API_BASE_URL}/organization/issue-certificate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      
      if (response.ok) {
        alert('Certificate issued successfully!');
        setCertificateForm({
          studentId: '',
          name: '',
          course: '',
          grade: '',
          duration: '',
          completionDate: ''
        });
        setCertificateTemplate(null);
        setStudentSearch('');
        setActiveTab('certificates');
        fetchUserData();
      } else {
        console.error('Issue error:', data);
        alert(data.error || 'Failed to issue certificate');
      }
    } catch (error) {
      console.error('Issue error:', error);
      alert('Failed to issue certificate. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verifyHash.trim()) {
      alert('Please enter a document hash');
      return;
    }
    
    if (verificationType === 'certificate' && !verifyTemplate && userType === 'organization') {
      alert('Please upload the certificate template for verification');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/verify/${verifyHash}`);
      const data = await response.json();
      setVerificationResult(data);
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationResult({ valid: false, message: 'Verification failed' });
    } finally {
      setIsLoading(false);
    }
  };

  const startQRScanner = async () => {
    setIsScanning(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (error) {
      console.error('Camera error:', error);
      alert('Unable to access camera. Please check permissions.');
      setIsScanning(false);
    }
  };

  const stopQRScanner = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsScanning(false);
  };

  const generateQRCode = (text) => {
    const canvas = canvasRef.current;
    if (!canvas) return '';
    
    const ctx = canvas.getContext('2d');
    const size = 300;
    canvas.width = size;
    canvas.height = size;
    
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = '#000000';
    
    const qrSize = 8;
    const padding = 20;
    const cellSize = (size - 2 * padding) / qrSize;
    
    for (let i = 0; i < qrSize; i++) {
      for (let j = 0; j < qrSize; j++) {
        const hash = (text.charCodeAt((i * qrSize + j) % text.length) + i + j) % 2;
        if (hash === 1) {
          ctx.fillRect(
            padding + j * cellSize,
            padding + i * cellSize,
            cellSize - 1,
            cellSize - 1
          );
        }
      }
    }
    
    return canvas.toDataURL();
  };

  const downloadQRCode = (hash, filename) => {
    const qrDataUrl = generateQRCode(hash);
    const link = document.createElement('a');
    link.download = `${filename}-qr.png`;
    link.href = qrDataUrl;
    link.click();
  };

  const connectWallet = async () => {
    try {
      if (typeof window.ethereum !== 'undefined') {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        setWalletAddress(accounts[0]);
        localStorage.setItem('walletAddress', accounts[0]);
        alert('Wallet connected successfully!');
      } else {
        alert('Please install MetaMask to connect your wallet');
      }
    } catch (error) {
      console.error('Wallet connection error:', error);
      alert('Failed to connect wallet');
    }
  };

  const disconnectWallet = () => {
    setWalletAddress('');
    localStorage.removeItem('walletAddress');
    alert('Wallet disconnected');
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUserType(null);
    setToken('');
    setUserData(null);
    setActiveTab('dashboard');
    localStorage.clear();
  };

  // Landing Page - Only User Type Selection
  if (!userType && !isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <img 
                src="https://raw.githubusercontent.com/King11919/D-verification/refs/heads/main/Authentichain%20logo.png" 
                alt="AuthentiChain Logo" 
                className="w-16 h-16"
              />
              <h1 className="text-5xl font-bold text-white">AuthentiChain</h1>
            </div>
            <p className="text-blue-200 text-xl">Blockchain-Powered Document Verification System</p>
            <p className="text-blue-300 text-sm mt-2">Secure, Transparent, Immutable</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div 
              onClick={() => setUserType('organization')}
              className="bg-white rounded-3xl shadow-2xl p-10 cursor-pointer hover:shadow-blue-500/50 transition-all hover:scale-105 transform"
            >
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Building2 className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3 text-center">Organization</h3>
              <p className="text-gray-600 text-center mb-4">Schools, Colleges & Companies</p>
              <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <School className="w-4 h-4" />
                  <span>Schools</span>
                </div>
                <div className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  <span>Colleges</span>
                </div>
                <div className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  <span>Companies</span>
                </div>
              </div>
            </div>

            <div 
              onClick={() => setUserType('student')}
              className="bg-white rounded-3xl shadow-2xl p-10 cursor-pointer hover:shadow-green-500/50 transition-all hover:scale-105 transform"
            >
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-lg">
                <User className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3 text-center">Student</h3>
              <p className="text-gray-600 text-center mb-4">View and Manage Certificates</p>
              <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Award className="w-4 h-4" />
                  <span>Certificates</span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  <span>Verified</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 text-center">
            <p className="text-blue-200 text-sm">Powered by Blockchain Technology</p>
          </div>
        </div>
      </div>
    );
  }

  // Login/Signup Forms
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <button
              onClick={() => setUserType(null)}
              className="text-gray-600 hover:text-gray-800 mb-4 flex items-center gap-2 text-sm"
            >
              ← Back to Home
            </button>

            <div className="text-center mb-6">
              <div className={userType === 'organization' ? 'bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4' : 'bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4'}>
                {userType === 'organization' && <Building2 className="w-8 h-8 text-blue-600" />}
                {userType === 'student' && <User className="w-8 h-8 text-green-600" />}
              </div>
              <h2 className="text-2xl font-bold text-gray-800">
                {userType === 'organization' ? 'Organization' : 'Student'} 
                {' '}{authMode === 'login' ? 'Login' : 'Signup'}
              </h2>
            </div>

            {userType === 'organization' && authMode === 'signup' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Organization Type</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setOrgType('school')}
                    className={`py-3 px-4 rounded-lg border-2 transition ${
                      orgType === 'school' 
                        ? 'border-blue-600 bg-blue-50 text-blue-600' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <School className="w-6 h-6 mx-auto mb-1" />
                    <p className="text-xs font-medium">School</p>
                  </button>
                  <button
                    onClick={() => setOrgType('college')}
                    className={`py-3 px-4 rounded-lg border-2 transition ${
                      orgType === 'college' 
                        ? 'border-blue-600 bg-blue-50 text-blue-600' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Building2 className="w-6 h-6 mx-auto mb-1" />
                    <p className="text-xs font-medium">College</p>
                  </button>
                  <button
                    onClick={() => setOrgType('company')}
                    className={`py-3 px-4 rounded-lg border-2 transition ${
                      orgType === 'company' 
                        ? 'border-blue-600 bg-blue-50 text-blue-600' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Briefcase className="w-6 h-6 mx-auto mb-1" />
                    <p className="text-xs font-medium">Company</p>
                  </button>
                </div>
              </div>
            )}

            {authMode === 'login' && (
              <form onSubmit={(e) => handleLogin(e, userType)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your password"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50"
                >
                  {isLoading ? 'Logging in...' : 'Login'}
                </button>
              </form>
            )}

            {authMode === 'signup' && userType === 'organization' && (
              <form onSubmit={handleOrganizationSignup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
                  <input
                    type="text"
                    value={orgSignupData.organizationName}
                    onChange={(e) => setOrgSignupData({...orgSignupData, organizationName: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={orgSignupData.email}
                    onChange={(e) => setOrgSignupData({...orgSignupData, email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={orgSignupData.password}
                    onChange={(e) => setOrgSignupData({...orgSignupData, password: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                  <input
                    type="text"
                    value={orgSignupData.contactPerson}
                    onChange={(e) => setOrgSignupData({...orgSignupData, contactPerson: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={orgSignupData.phone}
                    onChange={(e) => setOrgSignupData({...orgSignupData, phone: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <textarea
                    value={orgSignupData.address}
                    onChange={(e) => setOrgSignupData({...orgSignupData, address: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="2"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50"
                >
                  {isLoading ? 'Registering...' : 'Register'}
                </button>
              </form>
            )}

            {authMode === 'signup' && userType === 'student' && (
              <form onSubmit={handleStudentSignup} className="space-y-4 max-h-96 overflow-y-auto pr-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={studentSignupData.name}
                    onChange={(e) => setStudentSignupData({...studentSignupData, name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={studentSignupData.email}
                    onChange={(e) => setStudentSignupData({...studentSignupData, email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={studentSignupData.password}
                    onChange={(e) => setStudentSignupData({...studentSignupData, password: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                  <input
                    type="text"
                    value={studentSignupData.registrationNumber}
                    onChange={(e) => setStudentSignupData({...studentSignupData, registrationNumber: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={studentSignupData.phone}
                    onChange={(e) => setStudentSignupData({...studentSignupData, phone: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={studentSignupData.address}
                    onChange={(e) => setStudentSignupData({...studentSignupData, address: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent Name</label>
                  <input
                    type="text"
                    value={studentSignupData.parentName}
                    onChange={(e) => setStudentSignupData({...studentSignupData, parentName: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50"
                >
                  {isLoading ? 'Registering...' : 'Register'}
                </button>
              </form>
            )}

            <div className="mt-4 text-center">
              <button
                onClick={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                {authMode === 'login' ? "Don't have an account? Sign up" : "Already have an account? Login"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard (Logged In)
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://raw.githubusercontent.com/King11919/D-verification/refs/heads/main/Authentichain%20logo.png" 
              alt="AuthentiChain Logo" 
              className="w-8 h-8"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-800">AuthentiChain</h1>
              <p className="text-xs text-gray-500 capitalize">{userType} Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-800">
                {userData?.name || userData?.organizationName || userData?.email}
              </p>
              <p className="text-xs text-gray-500">{userData?.email}</p>
            </div>
            
            {walletAddress ? (
              <div className="flex items-center gap-2">
                <div className="bg-green-50 px-3 py-2 rounded-lg">
                  <p className="text-xs text-green-600 font-medium">
                    {walletAddress.substring(0, 6)}...{walletAddress.substring(38)}
                  </p>
                </div>
                <button
                  onClick={disconnectWallet}
                  className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition text-sm"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <button
                onClick={connectWallet}
                className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition text-sm font-medium"
              >
                Connect Wallet
              </button>
            )}
            
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Organization Navigation */}
      {userType === 'organization' && (
        <div className="bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="flex gap-4">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`py-4 px-6 font-medium border-b-2 transition ${
                  activeTab === 'dashboard'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => setActiveTab('issue')}
                className={`py-4 px-6 font-medium border-b-2 transition ${
                  activeTab === 'issue'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Upload className="w-4 h-4 inline mr-2" />
                Issue Certificate
              </button>
              <button
                onClick={() => setActiveTab('certificates')}
                className={`py-4 px-6 font-medium border-b-2 transition ${
                  activeTab === 'certificates'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Certificates ({certificates.length})
              </button>
              <button
                onClick={() => setActiveTab('verify')}
                className={`py-4 px-6 font-medium border-b-2 transition ${
                  activeTab === 'verify'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Search className="w-4 h-4 inline mr-2" />
                Verify Document
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Student Navigation */}
      {userType === 'student' && (
        <div className="bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="flex gap-4">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`py-4 px-6 font-medium border-b-2 transition ${
                  activeTab === 'dashboard'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                My Certificates
              </button>
              <button
                onClick={() => setActiveTab('verify')}
                className={`py-4 px-6 font-medium border-b-2 transition ${
                  activeTab === 'verify'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Search className="w-4 h-4 inline mr-2" />
                Verify Document
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Organization Dashboard */}
      {userType === 'organization' && activeTab === 'dashboard' && (
        <div className="container mx-auto px-4 py-8">
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Total Certificates</p>
                  <h3 className="text-3xl font-bold text-gray-800 mt-1">{certificates.length}</h3>
                </div>
                <FileText className="w-12 h-12 text-blue-600 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Organization Type</p>
                  <h3 className="text-xl font-bold text-gray-800 mt-1 capitalize">{userData?.type}</h3>
                </div>
                {userData?.type === 'school' && <School className="w-12 h-12 text-blue-600 opacity-20" />}
                {userData?.type === 'college' && <Building2 className="w-12 h-12 text-purple-600 opacity-20" />}
                {userData?.type === 'company' && <Briefcase className="w-12 h-12 text-green-600 opacity-20" />}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Status</p>
                  <h3 className="text-xl font-bold text-green-600 mt-1">Active</h3>
                </div>
                <CheckCircle className="w-12 h-12 text-green-600 opacity-20" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Recent Certificates</h2>
            {certificates.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No certificates issued yet</p>
                <button 
                  onClick={() => setActiveTab('issue')}
                  className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition"
                >
                  Issue Your First Certificate
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {certificates.slice(0, 5).map((cert) => (
                  <div key={cert._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800">{cert.name}</h3>
                        <p className="text-sm text-gray-600">Student: {cert.studentName} • {cert.course}</p>
                      </div>
                      <span className="text-xs text-gray-500">{new Date(cert.issueDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Issue Certificate Tab */}
      {userType === 'organization' && activeTab === 'issue' && (
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Issue New Certificate</h2>
            
            <form onSubmit={handleIssueCertificate} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Student</label>
                <input
                  type="text"
                  value={studentSearch}
                  onChange={(e) => {
                    setStudentSearch(e.target.value);
                    searchStudents(e.target.value);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Search by name, email, or registration number"
                />
                
                {searchResults.length > 0 && (
                  <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg">
                    {searchResults.map((student) => (
                      <button
                        key={student._id}
                        type="button"
                        onClick={() => {
                          setCertificateForm({...certificateForm, studentId: student._id});
                          setStudentSearch(student.name);
                          setSearchResults([]);
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 transition border-b border-gray-100 last:border-b-0"
                      >
                        <p className="font-medium text-gray-800">{student.name}</p>
                        <p className="text-sm text-gray-600">{student.email} • {student.registrationNumber}</p>
                      </button>
                    ))}
                  </div>
                )}
                
                {certificateForm.studentId && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700">✓ Student selected: {studentSearch}</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Certificate Name</label>
                <input
                  type="text"
                  value={certificateForm.name}
                  onChange={(e) => setCertificateForm({...certificateForm, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Bachelor of Science in Computer Science"
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Course</label>
                  <input
                    type="text"
                    value={certificateForm.course}
                    onChange={(e) => setCertificateForm({...certificateForm, course: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Computer Science"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Grade</label>
                  <input
                    type="text"
                    value={certificateForm.grade}
                    onChange={(e) => setCertificateForm({...certificateForm, grade: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., A+"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
                  <input
                    type="text"
                    value={certificateForm.duration}
                    onChange={(e) => setCertificateForm({...certificateForm, duration: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 4 years"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Completion Date</label>
                  <input
                    type="date"
                    value={certificateForm.completionDate}
                    onChange={(e) => setCertificateForm({...certificateForm, completionDate: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Certificate Template (Optional)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-500 transition">
                  <input
                    type="file"
                    onChange={(e) => setCertificateTemplate(e.target.files[0])}
                    className="hidden"
                    id="certificate-template"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  <label
                    htmlFor="certificate-template"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="w-12 h-12 text-gray-400 mb-2" />
                    <p className="text-blue-600 hover:text-blue-700 font-medium">
                      {certificateTemplate ? certificateTemplate.name : 'Click to upload template'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG (max 5MB)</p>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || !certificateForm.studentId}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Issuing Certificate...' : certificateForm.studentId ? 'Issue Certificate' : 'Please Select a Student First'}
              </button>
              
              {!certificateForm.studentId && (
                <p className="text-sm text-red-600 text-center mt-2">
                  ⚠ Please search and select a student before issuing certificate
                </p>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Certificates List Tab (Organization) */}
      {userType === 'organization' && activeTab === 'certificates' && (
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Issued Certificates</h2>
            
            {certificates.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No certificates issued yet</p>
                <button 
                  onClick={() => setActiveTab('issue')}
                  className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition"
                >
                  Issue Certificate
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {certificates.map((cert) => (
                  <div key={cert._id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="bg-blue-100 rounded-lg p-2">
                            <Award className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-800 text-lg">{cert.name}</h3>
                            <p className="text-sm text-gray-600">{cert.certificateId}</p>
                          </div>
                        </div>
                        <div className="grid md:grid-cols-3 gap-3 text-sm text-gray-600">
                          <p><strong>Student:</strong> {cert.studentName}</p>
                          <p><strong>Course:</strong> {cert.course}</p>
                          <p><strong>Grade:</strong> {cert.grade}</p>
                          <p><strong>Duration:</strong> {cert.duration}</p>
                          <p><strong>Issued:</strong> {new Date(cert.issueDate).toLocaleDateString()}</p>
                          <p><strong>Status:</strong> <span className="text-green-600 capitalize">{cert.status}</span></p>
                        </div>
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">Document Hash</p>
                          <p className="text-xs font-mono break-all text-gray-700">{cert.documentHash}</p>
                        </div>
                      </div>
                      <div className="ml-6 flex flex-col items-center gap-2">
                        <img src={cert.qrCode} alt="QR Code" className="w-32 h-32 border-2 border-gray-200 rounded-lg" />
                        <button
                          onClick={() => downloadQRCode(cert.documentHash, cert.certificateId)}
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          <Download className="w-4 h-4" />
                          Download QR
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Verify Tab (Both Organization and Student) */}
      {activeTab === 'verify' && (
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Verify Document</h2>
            
            {userType === 'organization' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Verification Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setVerificationType('document')}
                    className={`py-3 px-4 rounded-lg border-2 transition ${
                      verificationType === 'document'
                        ? 'border-blue-600 bg-blue-50 text-blue-600'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <FileText className="w-6 h-6 mx-auto mb-1" />
                    <p className="text-sm font-medium">Document</p>
                  </button>
                  <button
                    onClick={() => setVerificationType('certificate')}
                    className={`py-3 px-4 rounded-lg border-2 transition ${
                      verificationType === 'certificate'
                        ? 'border-blue-600 bg-blue-50 text-blue-600'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Award className="w-6 h-6 mx-auto mb-1" />
                    <p className="text-sm font-medium">Certificate</p>
                  </button>
                </div>
              </div>
            )}

            {userType === 'organization' && verificationType === 'certificate' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Certificate Template
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    onChange={(e) => setVerifyTemplate(e.target.files[0])}
                    className="hidden"
                    id="verify-template-upload"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  <label
                    htmlFor="verify-template-upload"
                    className="cursor-pointer flex items-center justify-center gap-2 text-blue-600 hover:text-blue-700"
                  >
                    <Upload className="w-5 h-5" />
                    <span className="text-sm font-medium">
                      {verifyTemplate ? verifyTemplate.name : 'Choose template file'}
                    </span>
                  </label>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={verifyHash}
                  onChange={(e) => setVerifyHash(e.target.value)}
                  placeholder="Enter document hash or scan QR code"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={isScanning ? stopQRScanner : startQRScanner}
                  className={`${isScanning ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-600 hover:bg-gray-700'} text-white px-4 py-3 rounded-lg transition`}
                  title={isScanning ? 'Stop Scanner' : 'Start QR Scanner'}
                >
                  <Camera className="w-5 h-5" />
                </button>
                <button
                  onClick={handleVerify}
                  disabled={isLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition disabled:opacity-50"
                >
                  <Search className="w-5 h-5" />
                </button>
              </div>

              {isScanning && (
                <div className="relative bg-black rounded-lg overflow-hidden">
                  <video ref={videoRef} className="w-full h-64 object-cover" />
                  <div className="absolute inset-0 border-4 border-blue-500 m-8 pointer-events-none"></div>
                  <p className="absolute bottom-4 left-0 right-0 text-center text-white text-sm">
                    Position QR code within the frame
                  </p>
                </div>
              )}
            </div>
            
            {verificationResult && (
              <div className={`mt-6 p-6 rounded-lg ${verificationResult.valid ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                <div className="flex items-start gap-4">
                  {verificationResult.valid ? (
                    <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0 mt-1" />
                  ) : (
                    <XCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
                  )}
                  <div className="flex-1">
                    <h4 className={`font-bold text-lg mb-2 ${verificationResult.valid ? 'text-green-800' : 'text-red-800'}`}>
                      {verificationResult.valid ? 'Document Verified ✓' : 'Document Not Found'}
                    </h4>
                    {verificationResult.valid && verificationResult.certificate && (
                      <div className="text-sm text-gray-700 space-y-2">
                        <p><strong>Certificate ID:</strong> {verificationResult.certificate.id}</p>
                        <p><strong>Student:</strong> {verificationResult.certificate.studentName}</p>
                        <p><strong>Issuer:</strong> {verificationResult.certificate.issuer}</p>
                        <p><strong>Course:</strong> {verificationResult.certificate.course}</p>
                        <p><strong>Grade:</strong> {verificationResult.certificate.grade}</p>
                        <p><strong>Issue Date:</strong> {new Date(verificationResult.certificate.issueDate).toLocaleDateString()}</p>
                        <div className="mt-3 p-3 bg-white rounded">
                          <p className="text-xs text-gray-500 mb-1">Transaction Hash</p>
                          <p className="text-xs font-mono break-all">{verificationResult.certificate.transactionHash}</p>
                        </div>
                      </div>
                    )}
                    {!verificationResult.valid && (
                      <p className="text-sm text-red-700">{verificationResult.message}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Student Dashboard */}
      {userType === 'student' && activeTab === 'dashboard' && (
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">My Profile</h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500 mb-1">Name</p>
                <p className="font-medium text-gray-800">{userData?.name}</p>
              </div>
              <div>
                <p className="text-gray-500 mb-1">Email</p>
                <p className="font-medium text-gray-800">{userData?.email}</p>
              </div>
              <div>
                <p className="text-gray-500 mb-1">Unique ID</p>
                <p className="font-medium text-gray-800">{userData?.uniqueId}</p>
              </div>
              <div>
                <p className="text-gray-500 mb-1">Phone</p>
                <p className="font-medium text-gray-800">{userData?.phone || 'Not provided'}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">My Certificates ({certificates.length})</h2>
            
            {certificates.length === 0 ? (
              <div className="text-center py-12">
                <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No certificates received yet</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {certificates.map((cert) => (
                  <div key={cert._id} className="border-2 border-gray-200 rounded-xl p-6 hover:shadow-lg transition">
                    <div className="flex items-start justify-between mb-4">
                      <div className="bg-blue-100 rounded-lg p-3">
                        <Award className="w-8 h-8 text-blue-600" />
                      </div>
                      <div className="flex flex-col items-center">
                        <img src={cert.qrCode} alt="QR Code" className="w-24 h-24 border-2 border-gray-200 rounded-lg mb-2" />
                        <button
                          onClick={() => downloadQRCode(cert.documentHash, cert.certificateId)}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-medium"
                        >
                          <Download className="w-3 h-3" />
                          Download
                        </button>
                      </div>
                    </div>
                    <h3 className="font-bold text-gray-800 text-lg mb-2">{cert.name}</h3>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p><strong>ID:</strong> {cert.certificateId}</p>
                      <p><strong>Issuer:</strong> {cert.issuerName}</p>
                      <p><strong>Course:</strong> {cert.course}</p>
                      <p><strong>Grade:</strong> {cert.grade}</p>
                      <p><strong>Issued:</strong> {new Date(cert.issueDate).toLocaleDateString()}</p>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">Document Hash</p>
                      <p className="text-xs font-mono bg-gray-50 p-2 rounded break-all">{cert.documentHash}</p>
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-xs">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-green-600 font-medium">Verified on Blockchain</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default AuthentiChain;
